import { useState } from 'react';

export default function HomePage() {
  const [formData, setFormData] = useState({
    companyName: '',
    email: '',
    phone: '',
    website: '',
    volume: '',
    region: '',
    requirements: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isNavScrolled, setIsNavScrolled] = useState(false);

  useState(() => {
    const handleScroll = () => {
      setIsNavScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('companyName', formData.companyName);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('website', formData.website);
      formBody.append('volume', formData.volume);
      formBody.append('region', formData.region);
      formBody.append('requirements', formData.requirements);

      const response = await fetch('https://readdy.ai/api/form/d5oelhkl56fkiit4o2ag', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          companyName: '',
          email: '',
          phone: '',
          website: '',
          volume: '',
          region: '',
          requirements: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isNavScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://static.readdy.ai/image/a5e7a8d75a8d5f4ed40cacf34a3df597/11df6f839158c987a838fe6484b0b0b8.jpeg" 
              alt="Vortex Digital Assets" 
              className="h-10 w-10 object-contain"
            />
            <span className={`text-xl font-semibold transition-colors ${isNavScrolled ? 'text-gray-900' : 'text-white'}`}>
              Vortex Digital Assets
            </span>
          </div>
          
          <div className="hidden md:flex items-center gap-8">
            <button 
              onClick={() => scrollToSection('about')}
              className={`text-sm font-medium transition-colors hover:text-teal-600 ${isNavScrolled ? 'text-gray-600' : 'text-gray-200'}`}
            >
              About
            </button>
            <button 
              onClick={() => scrollToSection('partner')}
              className={`text-sm font-medium transition-colors hover:text-teal-600 ${isNavScrolled ? 'text-gray-600' : 'text-gray-200'}`}
            >
              Partner Inquiry
            </button>
            <a 
              href="mailto:sales.vortexdigital@gmail.com"
              className="bg-teal-600 text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-teal-700 transition-all whitespace-nowrap"
            >
              Contact Sales
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'url(https://public.readdy.ai/ai/img_res/57160bf2d8e376d8995bb7c927fb94b7.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        
        <div className="relative max-w-7xl mx-auto px-6 py-32 w-full">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="inline-block">
                <span className="text-teal-400 text-xs font-semibold tracking-widest uppercase">
                  ENTERPRISE DIGITAL DISTRIBUTION
                </span>
              </div>
              
              <h1 className="text-6xl md:text-7xl font-bold text-white leading-tight">
                Powering Global<br />
                Digital Asset<br />
                Distribution
              </h1>
              
              <p className="text-lg text-gray-300 max-w-xl leading-relaxed">
                High-volume virtual credit solutions and digital liquidity infrastructure for gaming platforms worldwide
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href="mailto:sales.vortexdigital@gmail.com"
                  className="bg-teal-600 text-white px-8 py-4 rounded-lg text-base font-semibold hover:bg-teal-700 transition-all flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  Contact Sales
                  <i className="ri-arrow-right-line text-xl"></i>
                </a>
                <button 
                  onClick={() => scrollToSection('about')}
                  className="text-teal-400 hover:text-teal-300 transition-colors flex items-center gap-2 justify-center whitespace-nowrap"
                >
                  View Partner Benefits
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>
            
            <div className="relative hidden md:block">
              <div className="relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=futuristic%203D%20visualization%20of%20interconnected%20glowing%20blue%20and%20cyan%20nodes%20with%20data%20streams%20and%20network%20connections%20representing%20digital%20asset%20distribution%20infrastructure%20on%20dark%20background%20with%20modern%20corporate%20technology%20aesthetic&width=800&height=800&seq=hero-visual-001&orientation=squarish"
                  alt="Digital Infrastructure"
                  className="w-full h-auto rounded-2xl"
                />
                
                <div className="absolute top-8 right-8 bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
                  <div className="text-3xl font-bold text-white">99.9%</div>
                  <div className="text-sm text-gray-300">Uptime SLA</div>
                </div>
                
                <div className="absolute bottom-8 left-8 bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
                  <div className="text-3xl font-bold text-white">24/7</div>
                  <div className="text-sm text-gray-300">Support</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-5 gap-16 items-start">
            <div className="md:col-span-2">
              <div className="inline-block mb-6">
                <span className="bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-xs font-semibold tracking-wider uppercase">
                  WHO WE ARE
                </span>
              </div>
              
              <h2 className="text-5xl font-bold text-gray-900 leading-tight mb-8">
                Enterprise-Grade<br />
                Digital Infrastructure
              </h2>
            </div>
            
            <div className="md:col-span-3 space-y-8">
              <p className="text-lg text-gray-700 leading-relaxed">
                Vortex Digital Assets is a <strong>B2B Digital Asset Distributor and Liquidity Provider for Global Gaming Communities</strong>. We specialize in high-volume virtual credit top-ups and digital liquidity solutions that power seamless operations for gaming platforms worldwide.
              </p>
              
              <p className="text-lg text-gray-700 leading-relaxed">
                Our platform processes millions of transactions daily, delivering instant settlement and multi-region support with industry-leading reliability. Built on cutting-edge technology, we provide the backbone for gaming platforms to scale their digital economies efficiently and securely.
              </p>
              
              <p className="text-lg text-gray-700 leading-relaxed">
                With enterprise security standards and comprehensive compliance frameworks, we ensure your digital asset operations meet the highest industry requirements. Our dedicated support team works around the clock to keep your business running smoothly.
              </p>
            </div>
          </div>
          
          <div className="mt-16">
            <img 
              src="https://readdy.ai/api/search-image?query=modern%20corporate%20office%20environment%20with%20technology%20professionals%20working%20on%20digital%20infrastructure%20and%20data%20analytics%20displays%20showing%20clean%20minimalist%20workspace%20with%20blue%20accent%20lighting%20and%20professional%20business%20atmosphere&width=1400&height=600&seq=about-visual-001&orientation=landscape"
              alt="Our Infrastructure"
              className="w-full h-96 object-cover rounded-2xl shadow-xl"
            />
          </div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-teal-600 text-xs font-semibold tracking-widest uppercase">
              OUR CAPABILITIES
            </span>
            <h2 className="text-5xl font-bold text-gray-900 mt-4">
              Built for Scale
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 border border-gray-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="w-20 h-20 bg-teal-600 rounded-full flex items-center justify-center mb-6">
                <i className="ri-flashlight-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Instant Settlement
              </h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Process high-volume transactions with real-time settlement capabilities. Our infrastructure handles millions of operations daily with sub-second response times and guaranteed delivery.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-8 border border-gray-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="w-20 h-20 bg-cyan-500 rounded-full flex items-center justify-center mb-6">
                <i className="ri-global-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Multi-Region Support
              </h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Global coverage with localized payment methods and currency support. Our distributed infrastructure ensures low-latency access and compliance with regional regulations worldwide.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-8 border border-gray-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="w-20 h-20 bg-slate-800 rounded-full flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Enterprise Security
              </h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Bank-level security with comprehensive compliance frameworks. SOC 2 certified infrastructure with advanced fraud detection, encryption, and continuous monitoring to protect your operations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Partner Inquiry Section */}
      <section id="partner" className="py-24 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)',
          backgroundSize: '30px 30px'
        }}></div>
        
        <div className="relative max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-white mb-4">
              Ready to Scale Your<br />
              Digital Distribution?
            </h2>
            <p className="text-xl text-gray-300">
              Join leading gaming platforms leveraging our infrastructure
            </p>
          </div>
          
          <div className="bg-white rounded-2xl p-12 shadow-2xl">
            <form id="partner-inquiry-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    name="companyName"
                    required
                    value={formData.companyName}
                    onChange={(e) => setFormData({...formData, companyName: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base text-gray-900 focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all"
                    placeholder="Your company name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Contact Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base text-gray-900 focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all"
                    placeholder="email@company.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base text-gray-900 focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all"
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Company Website
                  </label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={(e) => setFormData({...formData, website: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base text-gray-900 focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all"
                    placeholder="https://yourcompany.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Monthly Volume Estimate *
                  </label>
                  <select
                    name="volume"
                    required
                    value={formData.volume}
                    onChange={(e) => setFormData({...formData, volume: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base text-gray-900 focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all"
                  >
                    <option value="">Select volume range</option>
                    <option value="under-100k">Under $100K</option>
                    <option value="100k-500k">$100K - $500K</option>
                    <option value="500k-1m">$500K - $1M</option>
                    <option value="1m-5m">$1M - $5M</option>
                    <option value="5m-plus">$5M+</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Region of Operation *
                  </label>
                  <select
                    name="region"
                    required
                    value={formData.region}
                    onChange={(e) => setFormData({...formData, region: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base text-gray-900 focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all"
                  >
                    <option value="">Select primary region</option>
                    <option value="north-america">North America</option>
                    <option value="europe">Europe</option>
                    <option value="asia-pacific">Asia Pacific</option>
                    <option value="latin-america">Latin America</option>
                    <option value="middle-east">Middle East</option>
                    <option value="global">Global</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Tell us about your requirements
                </label>
                <textarea
                  name="requirements"
                  value={formData.requirements}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) {
                      setFormData({...formData, requirements: e.target.value});
                    }
                  }}
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base text-gray-900 focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all resize-none"
                  placeholder="Describe your digital asset distribution needs, expected volume, and any specific requirements..."
                ></textarea>
                <div className="text-right text-sm text-gray-500 mt-1">
                  {formData.requirements.length}/500 characters
                </div>
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-teal-600 text-white px-8 py-4 rounded-lg text-base font-semibold hover:bg-teal-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
                <i className="ri-arrow-right-line text-xl"></i>
              </button>
              
              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                  Thank you! Your inquiry has been submitted successfully. Our team will respond within 24 business hours.
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                  There was an error submitting your inquiry. Please try again or contact us directly.
                </div>
              )}
              
              <p className="text-sm text-gray-500 text-center">
                Our team will respond within 24 business hours
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="https://static.readdy.ai/image/a5e7a8d75a8d5f4ed40cacf34a3df597/11df6f839158c987a838fe6484b0b0b8.jpeg" 
                  alt="Vortex Digital Assets" 
                  className="h-10 w-10 object-contain"
                />
                <span className="text-lg font-semibold">Vortex Digital Assets</span>
              </div>
              <p className="text-sm text-gray-400 leading-relaxed mb-4">
                B2B Digital Asset Distributor and Liquidity Provider for Global Gaming Communities.
              </p>
              <div className="space-y-2 mb-4">
                <div className="flex items-start gap-2 text-sm text-gray-400">
                  <i className="ri-map-pin-line mt-0.5"></i>
                  <div>
                    <div className="font-medium text-white">Vortex Digital Asset</div>
                    <div>100 King Street West, Suite 5700</div>
                    <div>Toronto, ON M5X 1C7, Canada</div>
                  </div>
                </div>
              </div>
              <div className="space-y-2 mb-4">
                <a href="mailto:sales.vortexdigital@gmail.com" className="flex items-center gap-2 text-sm text-gray-400 hover:text-teal-400 transition-colors">
                  <i className="ri-mail-line"></i>
                  sales.vortexdigital@gmail.com
                </a>
                <a href="https://wa.me/14472962159" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-gray-400 hover:text-teal-400 transition-colors">
                  <i className="ri-whatsapp-line"></i>
                  +1 447-296-2159
                </a>
              </div>
              
              {/* 24/7 Support Badge */}
              <div className="bg-teal-600 rounded-lg p-4 border-2 border-teal-500">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-customer-service-2-line text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <div className="text-xs font-bold text-white uppercase tracking-wide mb-1">
                      24/7 Global Support
                    </div>
                    <div className="text-xs text-teal-50 mb-2">
                      Toll-Free via WhatsApp
                    </div>
                    <a 
                      href="https://wa.me/14472962159" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm font-bold text-white hover:text-teal-100 transition-colors flex items-center gap-1"
                    >
                      +1 (447) 296-2159
                      <i className="ri-arrow-right-up-line text-base"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="text-xs font-semibold tracking-wider uppercase text-gray-400 mb-4">
                COMPANY
              </h4>
              <ul className="space-y-3">
                <li>
                  <button onClick={() => scrollToSection('about')} className="text-sm hover:text-teal-400 transition-colors cursor-pointer">
                    About Us
                  </button>
                </li>
                <li>
                  <a href="mailto:sales.vortexdigital@gmail.com" className="text-sm hover:text-teal-400 transition-colors">
                    Contact Sales
                  </a>
                </li>
                <li>
                  <button onClick={() => scrollToSection('partner')} className="text-sm hover:text-teal-400 transition-colors cursor-pointer">
                    Partner Program
                  </button>
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-xs font-semibold tracking-wider uppercase text-gray-400 mb-4">
                LEGAL
              </h4>
              <ul className="space-y-3">
                <li>
                  <a href="/terms-of-service" className="text-sm hover:text-teal-400 transition-colors">
                    Terms of Service
                  </a>
                </li>
                <li>
                  <a href="/refund-policy" className="text-sm hover:text-teal-400 transition-colors">
                    Refund Policy
                  </a>
                </li>
                <li>
                  <a href="/privacy-policy" className="text-sm hover:text-teal-400 transition-colors">
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <button onClick={() => scrollToSection('about')} className="text-sm hover:text-teal-400 transition-colors cursor-pointer">
                    Compliance
                  </button>
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-xs font-semibold tracking-wider uppercase text-gray-400 mb-4">
                CONNECT
              </h4>
              <ul className="space-y-3">
                <li>
                  <a href="#" className="text-sm hover:text-teal-400 transition-colors">
                    LinkedIn
                  </a>
                </li>
                <li>
                  <a href="#" className="text-sm hover:text-teal-400 transition-colors">
                    Twitter
                  </a>
                </li>
                <li>
                  <a href="mailto:sales.vortexdigital@gmail.com" className="text-sm hover:text-teal-400 transition-colors">
                    Email Us
                  </a>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-700 pt-8 mb-8">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Legal Policies - Left Column */}
              <div className="space-y-6">
                <div className="bg-slate-700/50 rounded-lg p-6 border border-gray-600">
                  <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                    <i className="ri-file-text-line text-teal-400"></i>
                    Terms of Service
                  </h4>
                  <p className="text-xs text-gray-300 leading-relaxed">
                    Vortex Digital Asset (vortexdigitalasset.com) provides digital asset liquidity and gaming credit infrastructure to international partners. By using our services, you agree that you are at least 18 years of age and are using our platform for its intended digital provisioning purposes.
                  </p>
                  <a 
                    href="/terms-of-service"
                    className="text-xs text-teal-400 hover:text-teal-300 mt-3 flex items-center gap-1"
                  >
                    Read Full Terms <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
                
                <div className="bg-slate-700/50 rounded-lg p-6 border border-gray-600">
                  <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                    <i className="ri-refund-line text-teal-400"></i>
                    Refund Policy
                  </h4>
                  <p className="text-xs text-gray-300 leading-relaxed">
                    Vortex Digital Asset provides immediate digital delivery of all credits and assets. Due to the nature of instant provisioning and the immediate consumption of digital services, all sales are final.
                  </p>
                  <a 
                    href="/refund-policy"
                    className="text-xs text-teal-400 hover:text-teal-300 mt-3 flex items-center gap-1"
                  >
                    Read Full Policy <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </div>
              
              {/* Payment Methods - Right Column */}
              <div className="flex flex-col justify-center">
                <h4 className="text-xs font-semibold tracking-wider uppercase text-gray-400 mb-4">
                  ACCEPTED PAYMENT METHODS
                </h4>
                <div className="flex flex-wrap items-center gap-4">
                  <div className="bg-white rounded px-3 py-2 flex items-center justify-center h-12 w-16">
                    <i className="ri-visa-line text-3xl text-blue-600"></i>
                  </div>
                  <div className="bg-white rounded px-3 py-2 flex items-center justify-center h-12 w-16">
                    <i className="ri-mastercard-line text-3xl text-orange-600"></i>
                  </div>
                  <div className="bg-white rounded px-3 py-2 flex items-center justify-center h-12 w-16">
                    <i className="ri-bank-card-line text-3xl text-teal-600"></i>
                  </div>
                  <div className="bg-white rounded px-3 py-2 flex items-center justify-center h-12 w-16">
                    <i className="ri-apple-fill text-3xl text-gray-900"></i>
                  </div>
                  <div className="bg-white rounded px-3 py-2 flex items-center justify-center h-12 w-16">
                    <i className="ri-google-fill text-3xl text-red-600"></i>
                  </div>
                  <div className="bg-white rounded px-3 py-2 flex items-center justify-center h-12 w-16">
                    <i className="ri-money-dollar-circle-line text-3xl text-green-600"></i>
                  </div>
                  <div className="bg-white rounded px-3 py-2 flex items-center justify-center h-12 w-16">
                    <i className="ri-wallet-3-line text-3xl text-green-700"></i>
                  </div>
                  <div className="bg-white rounded px-3 py-2 flex items-center justify-center h-12 w-16">
                    <i className="ri-exchange-dollar-line text-3xl text-blue-500"></i>
                  </div>
                  <div className="bg-white rounded px-3 py-2 flex items-center justify-center h-12 w-16">
                    <i className="ri-secure-payment-line text-3xl text-gray-700"></i>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-4 leading-relaxed">
                  Enterprise-grade payment processing with bank-level security and compliance standards.
                </p>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-700 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="text-7xl font-bold text-gray-700 tracking-tighter">
                VORTEX
              </div>
              <p className="text-xs text-gray-500">
                © 2026 Vortex Digital Assets. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Legal Sections */}
      <section id="terms" className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">Terms of Service</h2>
          <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
            <p className="text-base text-gray-700 leading-relaxed">
              Vortex Digital Asset (vortexdigitalasset.com) provides digital asset liquidity and gaming credit infrastructure to international partners. By using our services, you agree that you are at least 18 years of age and are using our platform for its intended digital provisioning purposes.
            </p>
            <a 
              href="/terms-of-service"
              className="inline-flex items-center gap-2 mt-4 text-teal-600 hover:text-teal-700 font-medium"
            >
              Read Full Terms <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </section>

      <section id="refund" className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">Refund Policy</h2>
          <div className="bg-gray-50 rounded-xl p-8 shadow-sm border border-gray-200">
            <p className="text-base text-gray-700 leading-relaxed">
              Vortex Digital Asset provides immediate digital delivery of all credits and assets. Due to the nature of instant provisioning and the immediate consumption of digital services, all sales are final.
            </p>
            <a 
              href="/refund-policy"
              className="inline-flex items-center gap-2 mt-4 text-teal-600 hover:text-teal-700 font-medium"
            >
              Read Full Policy <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </section>

      <section id="privacy" className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">Privacy Policy</h2>
          <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
            <p className="text-base text-gray-700 leading-relaxed">
              We take your privacy seriously. Vortex Digital Asset uses industry-standard SSL encryption to protect all transactional data. We do not store full credit card details on our servers, and we never sell client information to third-party marketing firms.
            </p>
            <a 
              href="/privacy-policy"
              className="inline-flex items-center gap-2 mt-4 text-teal-600 hover:text-teal-700 font-medium"
            >
              Read Full Policy <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}